import { useCallback, useEffect, useState } from 'react';
import {
  createCommissionFromDeal,
  deleteFundingCommission,
  fetchClientFundingCommissions,
  fetchDealFundingCommissions,
  fetchFundingCommission,
  fetchFundingCommissions,
  updateFundingCommission,
} from '../../services/funding/fundingCommissionsService';

export function useFundingCommissions({ agencyId, clientId, dealId, filters = {}, autoLoad = true } = {}) {
  const [commissions, setCommissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      let result;
      if (dealId && agencyId) {
        result = await fetchDealFundingCommissions(dealId, agencyId);
      } else if (clientId && agencyId) {
        result = await fetchClientFundingCommissions(clientId, agencyId);
      } else {
        result = await fetchFundingCommissions({
          agencyId,
          status: filters.status,
          payoutStatus: filters.payoutStatus,
          clawbackRisk: filters.clawbackRisk,
          assignedUserId: filters.assignedUserId,
          payoutDueFrom: filters.payoutDueFrom,
          payoutDueTo: filters.payoutDueTo,
          search: filters.search,
        });
      }
      setCommissions(result.commissions);
      return result.commissions;
    } catch (err) {
      setError(err?.message || 'Failed to load commissions');
      setCommissions([]);
      return [];
    } finally {
      setLoading(false);
    }
  }, [
    agencyId, clientId, dealId,
    filters.status, filters.payoutStatus, filters.clawbackRisk,
    filters.assignedUserId, filters.payoutDueFrom, filters.payoutDueTo, filters.search,
  ]);

  const createFromDeal = useCallback(async (targetDealId, targetAgencyId, payload = {}) => {
    const did = targetDealId || dealId;
    const aid = targetAgencyId || agencyId;
    if (!did || !aid) return null;

    setSaving(true);
    setError(null);
    try {
      const result = await createCommissionFromDeal(did, aid, payload);
      await load();
      return result;
    } catch (err) {
      setError(err?.message || 'Failed to create commission');
      throw err;
    } finally {
      setSaving(false);
    }
  }, [agencyId, dealId, load]);

  const update = useCallback(async (commissionId, payload, targetAgencyId) => {
    const aid = targetAgencyId || agencyId;
    if (!commissionId || !aid) return null;

    setSaving(true);
    setError(null);
    try {
      const result = await updateFundingCommission(commissionId, aid, payload);
      await load();
      return result;
    } catch (err) {
      setError(err?.message || 'Failed to update commission');
      throw err;
    } finally {
      setSaving(false);
    }
  }, [agencyId, load]);

  const remove = useCallback(async (commissionId, targetAgencyId) => {
    const aid = targetAgencyId || agencyId;
    if (!commissionId || !aid) return null;

    setSaving(true);
    setError(null);
    try {
      await deleteFundingCommission(commissionId, aid);
      await load();
      return true;
    } catch (err) {
      setError(err?.message || 'Failed to void commission');
      throw err;
    } finally {
      setSaving(false);
    }
  }, [agencyId, load]);

  useEffect(() => {
    if (autoLoad && (agencyId || clientId || dealId)) {
      load();
    }
  }, [autoLoad, load, agencyId, clientId, dealId]);

  return {
    commissions,
    loading,
    saving,
    error,
    load,
    createFromDeal,
    update,
    remove,
    setCommissions,
  };
}

export function useFundingCommissionDetail({ commissionId, agencyId, autoLoad = true } = {}) {
  const [commission, setCommission] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!commissionId || !agencyId) {
      setCommission(null);
      return null;
    }

    setLoading(true);
    setError(null);
    try {
      const result = await fetchFundingCommission(commissionId, agencyId);
      setCommission(result);
      return result;
    } catch (err) {
      setError(err?.message || 'Failed to load commission');
      setCommission(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, [commissionId, agencyId]);

  useEffect(() => {
    if (autoLoad) load();
  }, [autoLoad, load]);

  return { commission, loading, error, load, setCommission };
}
